import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, ShoppingBag, ArrowRight, ShoppingCart, Ruler } from 'lucide-react';
import {
  getCart,
  removeFromCart,
  createOrder,
  recalculateCart,
  getCheckoutUi,
  DEFAULT_CHECKOUT_UI,
  resolveBackendMediaUrl,
  importManagerCartToCrmOrder,
  type CheckoutUiCopy,
} from '../api';
import { useAppStore } from '../store';
import { useOrderComposeStore } from '../orderComposeStore';
import { OrderComposeBanner } from '../components/admin/OrderComposeBanner';
import { useInvalidateCrm } from '../hooks/crm/useCrmQueries';
import { getCategoryMeta } from '../categoryAssets';
import { useHaptic } from '../hooks/useHaptic';
import type { CartItem } from '../types';
import { cartScrollPadCompact, cartScrollPadExpanded, cartScrollPadHidden } from '../lib/appLayout';
import { formatOrderDimensionLines } from '../lib/dimensions';

function formatV2Details(params: Record<string, unknown> | undefined): string[] {
  if (!params || !params.v2_system_slug) return [];
  const parts: string[] = [];
  const protocol = typeof params.v2_protocol === 'string' ? params.v2_protocol : '';
  const color = typeof params.v2_color === 'string' ? params.v2_color : '';
  const opening = typeof params.opening_type === 'string' ? params.opening_type : '';
  if (protocol) parts.push(`Протокол: ${protocol}`);
  if (color) parts.push(`Цвет: ${color}`);
  if (opening === 'center') parts.push('Открывание: к центру');
  if (opening === 'left') parts.push('Открывание: влево');
  if (opening === 'right') parts.push('Открывание: вправо');
  if (params.v2_price_on_request) parts.push('Цена: по запросу');
  return parts;
}

function CartItemThumb({ item }: { item: CartItem }) {
  const categoryMeta = getCategoryMeta(item.product_category);
  const thumbSrc = item.thumbnail_url ? resolveBackendMediaUrl(item.thumbnail_url) : '';
  return (
    <div
      className="w-14 h-14 rounded-xl flex items-center justify-center text-lg shrink-0 overflow-hidden"
      style={{ background: 'var(--sapphire-card)', border: '1px solid var(--border-glass)' }}
    >
      {thumbSrc ? (
        <img
          src={thumbSrc}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
            const parent = e.currentTarget.parentElement;
            if (parent) parent.textContent = categoryMeta.emoji;
          }}
        />
      ) : (
        categoryMeta.emoji
      )}
    </div>
  );
}

function isLegacyOpeningSurcharge(row: unknown): boolean {
  if (!row || typeof row !== 'object') return false;
  const code = String((row as { code?: unknown }).code ?? '').toLowerCase();
  if (code === 'opening') return true;
  const label = String((row as { label?: unknown }).label ?? '').toLowerCase();
  return label.includes('симметричн') || label.includes('односторонн');
}

function readV2Breakdown(params: Record<string, unknown> | undefined): { base: number | null; surcharges: Array<{ label: string; amount: number }> } {
  if (!params || !params.v2_system_slug) return { base: null, surcharges: [] };
  const baseRaw = Number(params.v2_base_price);
  const base = Number.isFinite(baseRaw) ? baseRaw : null;
  const src = Array.isArray(params.surcharges) ? params.surcharges : [];
  const surcharges = src
    .filter((s) => !isLegacyOpeningSurcharge(s))
    .map((s) => {
      const label = String((s as any)?.label ?? '');
      const amount = Number((s as any)?.amount ?? 0);
      return { label, amount };
    })
    .filter((s) => s.label && Number.isFinite(s.amount) && s.amount > 0);
  return { base, surcharges };
}

export default function CartPage() {
  const navigate = useNavigate();
  const { token, setCart, setDiscount } = useAppStore();
  const [cartDiscountPct, setCartDiscountPct] = useState(0);
  const composeTarget = useOrderComposeStore((s) => s.target);
  const clearComposeTarget = useOrderComposeStore((s) => s.clearTarget);
  const invalidateCrm = useInvalidateCrm();
  const haptic = useHaptic();
  const composeMode = Boolean(composeTarget);
  const [items, setItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [ordering, setOrdering] = useState(false);
  const [ordered, setOrdered] = useState(false);
  const [composeDone, setComposeDone] = useState<string | null>(null);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [checkoutCopy, setCheckoutCopy] = useState<CheckoutUiCopy>(DEFAULT_CHECKOUT_UI);
  const [showCheckoutDock, setShowCheckoutDock] = useState(false);
  const [checkoutExpanded, setCheckoutExpanded] = useState(false);
  const [orderError, setOrderError] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const checkoutSentinelRef = useRef<HTMLDivElement>(null);

  const loadCart = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      await recalculateCart();
      const data = await getCart();
      setItems(data.items);
      setCart(data.items);
      setCartDiscountPct(data.discount_pct);
      setDiscount(data.discount_pct);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadCart();
  }, [token]);

  useEffect(() => {
    getCheckoutUi()
      .then((data) => setCheckoutCopy({ ...DEFAULT_CHECKOUT_UI, ...data }))
      .catch(() => setCheckoutCopy(DEFAULT_CHECKOUT_UI));
  }, []);

  const syncCheckoutDock = useCallback(() => {
    const root = scrollRef.current;
    const sentinel = checkoutSentinelRef.current;
    if (!root || !sentinel) return;
    const fits = root.scrollHeight <= root.clientHeight + 4;
    if (fits) {
      setShowCheckoutDock(true);
      return;
    }
    const viewportBottom = root.scrollTop + root.clientHeight;
    const sentinelBottom = sentinel.offsetTop + sentinel.offsetHeight;
    setShowCheckoutDock(viewportBottom >= sentinelBottom - 12);
  }, []);

  useEffect(() => {
    if (loading || items.length === 0) {
      if (!loading) {
        setShowCheckoutDock(false);
        setCheckoutExpanded(false);
      }
      return;
    }
    const root = scrollRef.current;
    if (!root) return;

    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(syncCheckoutDock);
    });
    root.addEventListener('scroll', syncCheckoutDock, { passive: true });
    const resizeObserver = new ResizeObserver(syncCheckoutDock);
    resizeObserver.observe(root);

    return () => {
      cancelAnimationFrame(raf);
      root.removeEventListener('scroll', syncCheckoutDock);
      resizeObserver.disconnect();
    };
  }, [items.length, loading, syncCheckoutDock]);

  useEffect(() => {
    if (!showCheckoutDock) setCheckoutExpanded(false);
  }, [showCheckoutDock]);

  const handleRemove = async (id: number) => {
    haptic.impact('medium');
    setItems(prev => prev.filter(i => i.id !== id));
      await removeFromCart(id);
      await loadCart();
  };

  const handleOrder = async () => {
    const address = deliveryAddress.trim();
    const name = clientName.trim();
    const phone = clientPhone.trim();
    const phoneDigits = phone.replace(/\D/g, '');
    if (address.length < 6) {
      setOrderError(checkoutCopy.error_address_short);
      haptic.warning();
      return;
    }
    if (name.length < 3) {
      setOrderError(checkoutCopy.error_name_short);
      haptic.warning();
      return;
    }
    if (phoneDigits.length < 10) {
      setOrderError(checkoutCopy.error_phone_short);
      haptic.warning();
      return;
    }
    setOrderError('');
    setOrdering(true);
    try {
      await createOrder({
        delivery_address: address,
        client_name: name,
        client_phone: phone,
      });
      haptic.success();
      setOrdered(true);
      setItems([]);
      setCart([]);
      setTimeout(() => navigate('/orders'), 1500);
    } catch (e: any) {
      console.error(e);
      setOrderError(e?.response?.data?.detail || 'Не удалось оформить заказ. Попробуйте ещё раз.');
      haptic.error();
    }
    finally { setOrdering(false); }
  };

  const handleImportToClientOrder = async () => {
    if (!composeTarget) return;
    setOrdering(true);
    setOrderError('');
    try {
      await importManagerCartToCrmOrder(composeTarget.orderId);
      haptic.success();
      const num = composeTarget.orderNumber;
      clearComposeTarget();
      void invalidateCrm();
      await loadCart();
      setComposeDone(num);
      setOrderError('');
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? String((err as { response?: { data?: { detail?: string } } }).response?.data?.detail ?? '')
          : '';
      setOrderError(msg || 'Не удалось добавить в заказ клиента');
      haptic.error();
    } finally {
      setOrdering(false);
    }
  };

  const handleCheckoutCta = () => {
    haptic.selection();
    if (composeMode) {
      void handleImportToClientOrder();
      return;
    }
    if (!checkoutExpanded) {
      setCheckoutExpanded(true);
      setOrderError('');
      requestAnimationFrame(() => {
        const root = scrollRef.current;
        if (root) {
          root.scrollTo({ top: root.scrollHeight, behavior: 'smooth' });
        }
        syncCheckoutDock();
      });
      return;
    }
    void handleOrder();
  };

  const total = items.reduce((s, i) => s + i.total_price, 0);
  const hasPriceOnRequestItems = items.some((i) => Boolean((i.custom_params as Record<string, unknown> | undefined)?.v2_price_on_request));
  const effectiveDiscountPct = Math.max(0, Number(cartDiscountPct || 0));
  const showRetail = effectiveDiscountPct > 0;
  const retailTotal = items.reduce(
    (s, i) =>
      s +
      (i.retail_unit_price != null && i.retail_unit_price > i.unit_price
        ? i.retail_unit_price * i.quantity
        : i.total_price),
    0,
  );
  const savingsTotal = Math.max(0, retailTotal - total);
  const finalTotal = total;

  const listScrollPad = !showCheckoutDock
    ? cartScrollPadHidden
    : checkoutExpanded
      ? cartScrollPadExpanded
      : cartScrollPadCompact;

  if (!token) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-5 px-8 text-center">
        <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: 'rgba(99,179,237,0.1)', border: '1px solid var(--border-glass)' }}>
          <ShoppingCart size={36} color="var(--cyan)" />
        </div>
        <div>
          <h2 className="text-lg font-bold mb-1" style={{ color: 'var(--text-1)' }}>Корзина недоступна</h2>
          <p className="text-sm" style={{ color: 'var(--text-2)' }}>Войдите через Telegram-бота</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--cyan)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (composeDone) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-5 px-8 text-center">
        <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: 'rgba(72,187,120,0.15)', border: '1px solid rgba(72,187,120,0.3)' }}>
          <span className="text-4xl">✓</span>
        </div>
        <div>
          <h2 className="text-lg font-bold mb-1" style={{ color: 'var(--text-1)' }}>Добавлено в {composeDone}</h2>
          <p className="text-sm" style={{ color: 'var(--text-2)' }}>Позиции перенесены в заказ клиента. Вернитесь в воронку, чтобы проверить состав.</p>
        </div>
        <button
          type="button"
          onClick={() => { haptic.selection(); setComposeDone(null); navigate('/admin'); }}
          className="btn-glow press px-6 py-3 rounded-2xl font-bold text-sm"
        >
          В админку
        </button>
      </div>
    );
  }

  if (ordered) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-5 px-8 text-center">
        <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: 'rgba(72,187,120,0.15)', border: '1px solid rgba(72,187,120,0.3)' }}>
          <span className="text-4xl">✓</span>
        </div>
        <div>
          <h2 className="text-lg font-bold mb-1" style={{ color: 'var(--text-1)' }}>Заказ оформлен</h2>
          <p className="text-sm" style={{ color: 'var(--text-2)' }}>Переходим к вашим заказам...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <OrderComposeBanner />
      <div className="header-glass px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold" style={{ color: 'var(--text-1)' }}>Корзина</h1>
          {items.length > 0 && (
            <span className="badge-cyan text-xs px-2.5 py-1 rounded-full font-semibold">{items.length} поз.</span>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center flex-1 gap-5 px-8 text-center">
          <div className="w-24 h-24 rounded-full flex items-center justify-center" style={{ background: 'var(--sapphire-card)', border: '1px solid var(--border-subtle)' }}>
            <ShoppingBag size={40} color="var(--text-3)" />
          </div>
          <div>
            <p className="font-bold text-lg mb-1" style={{ color: 'var(--text-1)' }}>Корзина пуста</p>
            <p className="text-sm" style={{ color: 'var(--text-2)' }}>Добавьте решения из каталога, чтобы собрать проект под ваш интерьер</p>
          </div>
          <div className="flex flex-col gap-2 w-full max-w-xs">
            <button
              onClick={() => { haptic.selection(); navigate('/catalog'); }}
              className="btn-glow text-white px-8 py-3 rounded-2xl font-semibold text-sm"
            >
              Открыть премиум-каталог
            </button>
            <button
              type="button"
              onClick={() => { haptic.selection(); navigate('/measurements?intent=measurement'); }}
              className="press px-8 py-3 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2"
              style={{ background: 'rgba(99,179,237,0.12)', border: '1px solid var(--cyan-border)', color: 'var(--cyan-bright)' }}
            >
              <Ruler size={18} /> Запись на замер
            </button>
          </div>
        </div>
      ) : (
        <>
          <div
            ref={scrollRef}
            className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3"
            style={{ paddingBottom: listScrollPad }}
          >
            <button
              type="button"
              onClick={() => { haptic.selection(); navigate('/measurements?intent=measurement'); }}
              className="press w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-left text-xs font-semibold mb-3"
              style={{ background: 'rgba(99,179,237,0.08)', border: '1px solid var(--border-glass)', color: 'var(--cyan)' }}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(99,179,237,0.14)' }}>
                <Ruler size={20} color="var(--cyan-bright)" />
              </div>
              <div className="min-w-0">
                <p className="font-bold" style={{ color: 'var(--text-1)' }}>Выезд технолога</p>
                <p className="text-[11px] font-normal mt-0.5" style={{ color: 'var(--text-3)' }}>Заявка на замер без оформления заказа — в профиле</p>
              </div>
            </button>

            {items.map((item) => (
              <div key={item.id} className="glass rounded-2xl overflow-hidden">
                <div className={`h-0.5 bg-gradient-to-r ${getCategoryMeta(item.product_category).gradient}`} />
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <CartItemThumb item={item} />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm leading-tight line-clamp-2" style={{ color: 'var(--text-1)' }}>{item.product_name}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>{item.product_category}</p>
                      {formatV2Details(item.custom_params as Record<string, unknown> | undefined).map((line) => (
                        <p key={`${item.id}-${line}`} className="text-[11px] mt-0.5" style={{ color: 'var(--cyan)' }}>
                          {line}
                        </p>
                      ))}
                    </div>
                    <button
                      aria-label="Удалить товар из корзины"
                      onClick={() => handleRemove(item.id)}
                      className="press p-1.5 rounded-xl shrink-0"
                      style={{ color: 'var(--text-3)' }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  {(() => {
                    const breakdown = readV2Breakdown(item.custom_params as Record<string, unknown> | undefined);
                    if (breakdown.base === null && breakdown.surcharges.length === 0) return null;
                    return (
                      <div className="mt-2.5 rounded-xl px-3 py-2 text-[11px]" style={{ background: 'rgba(99,179,237,0.08)', border: '1px solid var(--border-glass)' }}>
                        {breakdown.base !== null && (
                          <p style={{ color: 'var(--text-2)' }}>База: {breakdown.base.toLocaleString('ru-RU')} ₽</p>
                        )}
                        {breakdown.surcharges.map((s) => (
                          <p key={`${item.id}-${s.label}`} style={{ color: 'var(--text-3)' }}>
                            + {s.label}: {s.amount.toLocaleString('ru-RU')} ₽
                          </p>
                        ))}
                      </div>
                    );
                  })()}
                  <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <div className="flex flex-wrap gap-2 text-xs" style={{ color: 'var(--text-2)' }}>
                      {(() => {
                        const openingType = typeof item.custom_params?.['opening_type'] === 'string'
                          ? item.custom_params['opening_type']
                          : '';
                        return (
                          <>
                      {formatOrderDimensionLines({
                        length_m: item.length_m,
                        width_m: item.width_m,
                        custom_params: item.custom_params as Record<string, unknown> | undefined,
                      }).map((line) => (
                        <span key={`${item.id}-${line}`} className="w-full leading-snug block">{line}</span>
                      ))}
                      {openingType === 'center' && <span>↔ центр</span>}
                      {openingType === 'left' && <span>← влево</span>}
                      {openingType === 'right' && <span>→ вправо</span>}
                      <span>× {item.quantity}</span>
                          </>
                        );
                      })()}
                    </div>
                    <div className="text-right">
                      {((item.custom_params as Record<string, unknown> | undefined)?.v2_price_on_request) ? (
                        <span className="font-bold text-xs block" style={{ color: '#F6AD55' }}>Цена по запросу</span>
                      ) : (
                        <>
                          <span className="font-bold text-base block" style={{ color: 'var(--cyan-bright)' }}>{item.total_price.toLocaleString('ru-RU')} ₽</span>
                          {showRetail && item.retail_unit_price != null && item.retail_unit_price > item.unit_price && (
                            <span className="text-[10px] block" style={{ color: '#9AE6B4' }}>
                              Розница {Math.round(item.retail_unit_price * item.quantity).toLocaleString('ru-RU')} ₽
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div ref={checkoutSentinelRef} className="h-px w-full shrink-0" aria-hidden />
          </div>

          <div
            className={`cart-checkout-dock ${showCheckoutDock ? '' : 'cart-checkout-dock--hidden'} ${checkoutExpanded ? 'cart-checkout-dock--expanded' : 'cart-checkout-dock--compact'}`}
            aria-hidden={!showCheckoutDock}
          >
            <div className="cart-checkout-dock__expand" aria-hidden={!checkoutExpanded || composeMode}>
              <div className="cart-checkout-dock__expand-inner space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--cyan)' }}>
                    Данные для договора
                  </p>
                  <button
                    type="button"
                    onClick={() => { haptic.selection(); setCheckoutExpanded(false); setOrderError(''); }}
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-lg press"
                    style={{ color: 'var(--text-3)', border: '1px solid var(--border-subtle)' }}
                  >
                    Свернуть
                  </button>
                </div>
                <input
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  placeholder={checkoutCopy.address_placeholder}
                  className="input-glass w-full rounded-xl px-3 py-2.5 text-sm"
                  autoComplete="street-address"
                />
                <input
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder={checkoutCopy.client_name_placeholder}
                  className="input-glass w-full rounded-xl px-3 py-2.5 text-sm"
                  autoComplete="name"
                />
                <input
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  placeholder={checkoutCopy.client_phone_placeholder}
                  className="input-glass w-full rounded-xl px-3 py-2.5 text-sm"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                />
                {showRetail && savingsTotal > 0 && (
                  <div className="space-y-1 pt-0.5">
                    <div className="flex justify-between text-xs">
                      <span style={{ color: 'var(--text-2)' }}>{checkoutCopy.retail_total_label}</span>
                      <span style={{ color: 'var(--text-1)' }}>{Math.round(retailTotal).toLocaleString('ru-RU')} ₽</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span style={{ color: '#9AE6B4' }}>{checkoutCopy.savings_label} ({effectiveDiscountPct.toFixed(0)}%)</span>
                      <span style={{ color: '#9AE6B4' }}>−{Math.round(savingsTotal).toLocaleString('ru-RU')} ₽</span>
                    </div>
                  </div>
                )}
                {hasPriceOnRequestItems && (
                  <p className="text-xs leading-snug" style={{ color: '#F6AD55' }}>
                    {checkoutCopy.price_on_request_hint}
                  </p>
                )}
                {orderError && (
                  <p className="text-xs" style={{ color: '#FC814A' }}>{orderError}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="shrink-0 min-w-[88px]">
                <p className="text-[9px] uppercase tracking-widest font-semibold mb-0.5" style={{ color: 'var(--text-3)' }}>
                  {checkoutCopy.total_label}
                </p>
                <p className="text-lg font-black tabular-nums leading-tight" style={{ color: 'var(--cyan-bright)' }}>
                  {finalTotal.toLocaleString('ru-RU')}
                  <span className="text-sm font-semibold ml-0.5" style={{ color: 'var(--text-2)' }}>₽</span>
                </p>
                {showRetail && !checkoutExpanded && savingsTotal > 0 && (
                  <p className="text-[9px] mt-0.5 leading-tight" style={{ color: '#9AE6B4' }}>
                    −{Math.round(savingsTotal).toLocaleString('ru-RU')} ₽
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={handleCheckoutCta}
                disabled={ordering}
                className="btn-glow press flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl font-bold text-sm min-h-[48px] text-white"
              >
                {ordering ? (
                  <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'rgba(255,255,255,0.6)', borderTopColor: 'transparent' }} />
                ) : (
                  <>
                    <ArrowRight size={18} />
                    {composeMode
                      ? `В заказ ${composeTarget!.orderNumber}`
                      : checkoutExpanded
                        ? 'Подтвердить заказ'
                        : checkoutCopy.submit_button}
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
